// src/views/accueil/FilEvenements.jsx — CE QU'IL ANNONCE, AU FIL DE L'EAU.
//
// ══ LA DEMANDE ═════════════════════════════════════════════════════════════
//
// Dimitri, 2026-09-13 : « Aujourd'hui il attend mes questions. Je veux qu'il
// m'annonce ce qui se passe, au fil de l'eau. »
//
// ══ LA RÈGLE QUI COMPTE, ET C'EST LUI QUI LA NOMME AINSI ═══════════════════
//
// « Ce qui a une maison y va. Un message s'annonce, puis rejoint "Messages en
// attente" et disparaît du fil — il n'existe plus qu'une fois. Si le fil existe
// déjà, la ligne remonte au lieu d'être dupliquée. Ce qui n'a pas de maison
// reste dans le fil, pour que je ne le rate pas si je n'étais pas devant. »
//
// ⚠️ LE TRI SE FAIT ICI, PAS AU SERVEUR : c'est l'ÉCRAN qui sait ce qu'il
// affiche déjà (règle générale du 2026-09-12 — l'écran fait foi sur ses
// listes). Le serveur annonce ; l'écran retire ce qui a trouvé sa place.
//
// ══ LES ANIMATIONS — « si ça bouge, il s'est passé quelque chose » ═════════
//
// Les événements glissent EN ARRIVANT, une seule fois ; les précédents restent
// immobiles. On mémorise donc les clés déjà vues, plutôt que d'animer la liste
// à chaque rendu — c'est le défaut que Dimitri avait lui-même corrigé dans sa
// maquette. Et tout s'arrête si le système demande la réduction des animations.
//
// ══ L'HABILLAGE EST CELUI DE LA MAQUETTE (`.opEvL`, admin-proto.html) ══════
//
// Un événement n'est pas une carte : un cercle fin de la couleur de la famille,
// une étiquette en petites capitales, la phrase avec sa TÊTE en gras, un
// lien-bouton, et un ✕ pour l'écarter. Les valeurs (24px, 9.5px/.07em, 13px/1.55,
// `opEntre` avec son blur, `opSort` .45s) sont lues dans le fichier, pas
// approchées.

import { useEffect, useRef, useState } from "react";
import { DS } from "../../lib/ui";
import { dessinDe, scinderTete, PALETTE_OP } from "../../lib/evenements-dessin";
import { jourLocal } from "../../lib/evenements-poids";

const NOIR = PALETTE_OP.navyD;

// La durée de `opSort` dans la maquette. Le retrait réel attend la fin de
// l'animation — et un délai de secours de la même durée couvre le cas où elle
// ne joue pas (réduction des animations, ou jsdom).
const DUREE_SORTIE_MS = 450;

/**
 * ⚠️ UN TRAIT SE RANGE TOUT SEUL — APRÈS DOUZE MINUTES, PAS QUATRE.
 *
 * Dimitri, 2026-09-15, sur sa maquette (qui disait quatre) : « quatre minutes
 * c'est trop court, je peux m'absenter ». Il veut voir passer sa journée, pas
 * un fil qui s'allonge de trente lignes — mais il veut la voir.
 *
 * ⚠️ ET CE QUI PRESSE NE SE RANGE PAS. Un ménage sans agent reste jusqu'à ce
 * qu'il soit traité, même trois jours plus tard. Seuls les constats s'effacent
 * du fil, et ils rejoignent le bouton « il remarque » — jamais le néant.
 */
const RANGE_APRES_MS = 12 * 60 * 1000;
/** Le pas du balayage : ce qui a passé son temps s'en va dans les 30 s. */
const PAS_BALAYAGE_MS = 30 * 1000;
/** Au-delà, une ligne n'est plus une nouveauté : elle est là, c'est tout. */
const DUREE_NEUF_MS = 10 * 1000;

const STYLE = `@keyframes fbzEvEntre{from{opacity:0;transform:translateY(-10px) scale(.97);filter:blur(2px)}to{opacity:1;transform:none;filter:none}}
@keyframes fbzEvSort{to{opacity:0;transform:translateX(22px) scale(.97);max-height:0;padding-top:0;padding-bottom:0;margin-top:-13px}}
[data-fbz-ev="1"]{animation:fbzEvEntre .5s cubic-bezier(.22,1,.36,1)}
[data-fbz-ev="part"]{animation:fbzEvSort .45s ease-in forwards;pointer-events:none}
.fbzEvX{opacity:.45;transition:opacity .14s,background .14s}
[data-fbz-evl]:hover .fbzEvX{opacity:1}
.fbzEvX:hover{background:${PALETTE_OP.fond};color:${NOIR}}
.fbzEvGo:hover{text-decoration:underline;text-underline-offset:3px}
@media (prefers-reduced-motion:reduce){[data-fbz-ev]{animation:none!important}}
.fbzTrait{display:flex;align-items:baseline;gap:9px;padding:7px 4px;
 border-left:2px solid ${PALETTE_OP.bord};padding-left:11px;margin-left:2px;
 cursor:pointer;transition:border-color .16s,background .16s,opacity .5s,transform .5s;
 border-radius:0 8px 8px 0;text-align:left;width:100%;border-top:none;border-right:none;border-bottom:none;background:none}
.fbzTrait:hover{border-left-color:${PALETTE_OP.ciel};background:${PALETTE_OP.fond}}
.fbzTrait .h{font-size:9px;color:${PALETTE_OP.grisD};font-variant-numeric:tabular-nums;
 flex-shrink:0;width:32px;letter-spacing:.03em}
.fbzTrait .n{font-size:9px;letter-spacing:.07em;text-transform:uppercase;
 font-weight:700;color:${PALETTE_OP.grisD};flex-shrink:0}
.fbzTrait .t{flex:1;min-width:0;font-size:11.5px;line-height:1.45;color:${PALETTE_OP.gris};
 overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fbzTrait:hover .t{color:${NOIR};white-space:normal}
.fbzTrait .f{color:${PALETTE_OP.grisD};font-size:12px;flex-shrink:0}
/* Le rangement est VISIBLE : la ligne pâlit et glisse avant de partir. */
.fbzTrait.part{opacity:.3;transform:translateX(14px);pointer-events:none}
@media (prefers-reduced-motion:reduce){.fbzTrait{transition:none}}`;

export default function FilEvenements({
  // Ce que le fil montre, dans l'ORDRE DU TEMPS. Chaque ligne porte `presse` :
  // une CARTE si elle presse, un TRAIT FIN sinon (Dimitri, 2026-09-15 : « deux
  // niveaux suffisent »). Aucun plafond : « cacher une chose importante pour
  // garder un écran propre est une mauvaise affaire ».
  evenements,
  onGeste,
  onAller,
  // La ligne porte une FEUILLE : le bouton l'ouvre au lieu d'aller à un
  // écran, et la ligne quitte le fil (elle a trouvé sa maison).
  onOuvrir,
  // ── TRAITER ET RANGER — deux sorties, et aucune ne jette ────────────────
  // `onTraiter` : Dimitri a écarté la ligne (✕) ou a agi dessus. Elle quitte
  // le fil et reste dans la journée.
  // `onRanger` : le TEMPS l'a rangée. Un trait fin part au bouton « il
  // remarque » après quelques minutes ; une CARTE ne se range jamais toute
  // seule — un ménage sans agent reste jusqu'à ce qu'il soit fait, même trois
  // jours plus tard.
  onTraiter,
  onRanger,
  // ── LES TOURS DE LA CONVERSATION, MÊLÉS AUX ANNONCES ────────────────────
  // Dimitri, 2026-09-15 : « Ils devraient être DANS le fil, chacun à son
  // heure, mêlés à ce qu'on s'est dit. 07h15 un événement, 08h12 un autre,
  // 21h34 ma question, 21h34 sa réponse, 21h35 mon "oui". UNE SEULE
  // CHRONOLOGIE. C'est ce qui fait la différence entre une conversation et un
  // tableau de bord avec une zone de chat en haut. »
  // Les tours arrivent tels quels (`{role, texte, quand}`) et c'est
  // `rendreTour` qui les dessine : cet écran décide de leur PLACE, pas de
  // leur forme.
  tours,
  rendreTour,
  // Le délai avant qu'un trait se range, et le pas du balayage. Dimitri,
  // 2026-09-15 : « quatre minutes c'est trop court, je peux m'absenter ».
  rangeApresMs = RANGE_APRES_MS,
  // Le même dessin sert deux listes : le fil, et la feuille « il remarque ».
  testid = "operateur-evenements",
}) {
  // ── « CE QUI REVIENT TOUS LES JOURS SANS CHANGER, JE NE LE VOIS PLUS » ──
  //
  // Dimitri, 2026-09-15. Une ligne n'est NEUVE que si elle vient d'entrer
  // dans la journée — et « venir d'entrer » se lit sur `vu_le`, posé une
  // seule fois par le store (src/lib/evenements-jour.js) et conservé à chaque
  // ré-annonce.
  //
  // ⚠️ UNE `ref` NE SUFFISAIT PAS : elle meurt avec le composant. Au
  // rafraîchissement de trois minutes, à un retour d'onglet, à une
  // réouverture du panneau, TOUTES les lignes redevenaient « neuves » et
  // rejouaient leur animation — le mouvement sans raison qu'on refuse, et
  // pire : un même fait qui a l'air de se reproduire.
  const estNeuve = (e) =>
    Number.isFinite(e && e.vu_le) && Date.now() - e.vu_le < DUREE_NEUF_MS;

  // ── LA SORTIE EST VISIBLE ──────────────────────────────────────────────
  // Qu'on l'écarte ou que le temps la range, une ligne PÂLIT ET GLISSE avant
  // de partir (maquette : « une information qui disparaît sans prévenir donne
  // l'impression d'avoir raté quelque chose »). L'état est local et ne dure
  // que l'animation : ce qui décide de la disparition, c'est le parent.
  const [partants, setPartants] = useState(() => new Set());
  const minuteries = useRef(new Map());
  useEffect(
    () => () => {
      for (const t of minuteries.current.values()) clearTimeout(t);
    },
    [],
  );
  function finirDePartir(cle, suite) {
    const t = minuteries.current.get(cle);
    if (t) {
      clearTimeout(t);
      minuteries.current.delete(cle);
    }
    setPartants((v) => {
      if (!v.has(cle)) return v;
      const n = new Set(v);
      n.delete(cle);
      return n;
    });
    if (typeof suite === "function") suite(cle);
  }
  function sortir(cle, suite) {
    if (partants.has(cle)) return;
    setPartants((v) => new Set(v).add(cle));
    minuteries.current.set(
      cle,
      setTimeout(() => finirDePartir(cle, suite), DUREE_SORTIE_MS),
    );
  }

  // ── LE BALAYAGE — un trait se range tout seul, pas une carte ───────────
  // Toutes les 30 secondes, ce qui a passé son temps s'en va. ⚠️ CE QUI
  // PRESSE NE SE RANGE JAMAIS : seuls les constats s'effacent du fil.
  const listeRef = useRef(evenements);
  listeRef.current = evenements;
  const rangerRef = useRef(onRanger);
  rangerRef.current = onRanger;
  useEffect(() => {
    // ⚠️ PAS DE BALAYAGE SANS RANGEUR (revue, MAJEUR 7). Le panneau « il
    // remarque » monte la même liste SANS `onRanger` : le balayage y jouait
    // l'animation de sortie toutes les 30 secondes, puis la ligne revenait
    // (personne pour la ranger). Le panneau clignotait, et rien ne le disait.
    if (typeof onRanger !== "function") return undefined;
    const id = setInterval(() => {
      const maintenant = Date.now();
      for (const e of listeRef.current || []) {
        if (!e || e.presse === true || !e.vu_le) continue;
        if (maintenant - e.vu_le > rangeApresMs)
          sortir(e.cle, (c) => rangerRef.current && rangerRef.current(c));
      }
    }, PAS_BALAYAGE_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeApresMs, typeof onRanger === "function"]);

  // ⚠️ L'INSTANT, PAS LA CHAÎNE. Une date NUE (« 2026-09-16 », l'échéance
  // d'un état durable) se compare à minuit DU JOUR REGARDÉ : un ménage sans
  // agent de demain se place en tête de journée, pas au futur — sinon il
  // passerait après la conversation du soir, là où personne ne le lit.
  const AUJOURDHUI = jourLocal();
  const instantDe = (v) => {
    if (typeof v !== "string" || !v) return 0;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      const t = Date.parse(`${v}T00:00:00Z`);
      const t0 = Date.parse(`${AUJOURDHUI}T00:00:00Z`);
      // Une échéance à venir se pose au début de la journée en cours.
      return Number.isFinite(t) && t > t0 ? t0 : t || 0;
    }
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  };

  const liste = (evenements || []).filter(Boolean);
  const lesTours = (tours || []).filter(Boolean);
  if (!liste.length && !lesTours.length) return null;

  // Une seule liste, triée par le temps. À égalité, l'annonce passe avant le
  // tour : elle est ce qui a PROVOQUÉ la question, pas ce qui la suit.
  const chronologie = [
    ...liste.map((e) => ({ quand: instantDe(e.quand), rang: 0, e })),
    ...lesTours.map((t, i) => ({
      quand: instantDe(t.quand),
      rang: 1,
      t,
      i,
    })),
  ].sort((a, b) => a.quand - b.quand || a.rang - b.rang);

  // ── CE QUE FAIT UN CLIC SUR UNE LIGNE ──────────────────────────────────
  // UN SEUL AIGUILLAGE POUR LES DEUX DESSINS. La carte et le trait fin
  // doivent mener au même endroit : une ligne qui porte une feuille l'ouvre,
  // qu'elle presse ou non. (Sans ça, un constat non pressant qui porte une
  // feuille partirait vers un écran — le bouton mentirait, en silence.)
  const activer = (e) => {
    if (e.feuille) {
      if (onOuvrir) onOuvrir(e);
      // La même sortie visible que la croix : elle pâlit, glisse, puis quitte
      // la journée — elle vit maintenant dans la feuille ouverte.
      sortir(e.cle, (c) => onTraiter && onTraiter(c));
      return;
    }
    if (e.geste) onGeste && onGeste(e);
    else onAller && onAller(e);
  };

  // ⚠️ UNE SEULE FAÇON DE DESSINER UNE LIGNE. Les « autres qui pressent »
  // dépliés sont EXACTEMENT les mêmes lignes, avec leur geste et leur ✕ — pas
  // une version résumée qui divergerait au premier ajout.
  const ligne = (e) => {
    const d = dessinDe(e);
    const neuf = estNeuve(e);
    const part = partants.has(e.cle);
    const { tete, reste } = scinderTete(e.phrase);
    return (
      <div
        key={e.cle}
        data-testid={`evenement-${e.cle}`}
        data-famille={e.famille}
        data-fbz-evl="1"
        data-fbz-ev={part ? "part" : neuf ? "1" : undefined}
        onAnimationEnd={undefined}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 11,
          padding: "9px 2px 9px 0",
          overflow: part ? "hidden" : undefined,
          // « Ce qu'il remarque » : un trait plein à gauche, décalé, sur
          // fond blanc avec son liseré — ce n'est pas un fait qui tombe,
          // c'est lui qui parle (`.opEvL.pense`, maquette).
          ...(e.remarque
            ? {
                borderLeft: `2px solid ${PALETTE_OP.cielP}`,
                paddingLeft: 13,
                marginLeft: 11,
                background: "#fff",
                boxShadow: "inset 0 0 0 1px rgba(35,50,64,.07)",
              }
            : {}),
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            marginTop: 1,
            color: d.teinte,
            ...(e.remarque
              ? { background: PALETTE_OP.cielP, color: PALETTE_OP.navy }
              : { boxShadow: `inset 0 0 0 1.5px ${d.teinte}` }),
          }}
        >
          {d.icone}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: 9.5,
              letterSpacing: ".07em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 2,
              color: e.remarque ? PALETTE_OP.grisD : d.teinte,
            }}
          >
            {d.etiquette}
          </span>
          <span
            style={{
              display: "block",
              fontSize: 13,
              lineHeight: 1.55,
              color: NOIR,
            }}
          >
            {/* ⚠️ La tête en gras est un ÉLÉMENT React, jamais du HTML
                    injecté : la phrase vient du serveur en texte pur et le
                    reste. */}
            {tete ? (
              <b
                data-testid={`evenement-tete-${e.cle}`}
                style={{ fontWeight: 600 }}
              >
                {tete}
              </b>
            ) : null}
            {reste}
          </span>
          {/* ── SON GESTE ────────────────────────────────────────────
                  ⚠️ UN ÉVÉNEMENT SANS GESTE N'EN INVENTE PAS. Le bouton
                  n'apparaît que si le serveur a nommé un levier qui existe,
                  ou un écran qui existe. Un libellé qui ne mène nulle part
                  est pire que pas de bouton : on clique une fois, et on
                  n'essaie plus jamais.
                  ── ET QUAND LA LIGNE PORTE UNE FEUILLE, LE BOUTON FAIT DEUX
                  CHOSES : il OUVRE la feuille ET RETIRE la ligne du fil (même
                  sortie visible que la croix) — elle a trouvé sa maison, elle
                  n'existe plus qu'une fois. La feuille passe AVANT le geste
                  nu : c'est elle qui porte le geste, avec son contexte. */}
          {e.feuille || e.geste || e.ecran ? (
            <button
              type="button"
              className="fbzEvGo"
              data-testid={`evenement-geste-${e.cle}`}
              onClick={() => activer(e)}
              style={{
                marginTop: 6,
                minHeight: 28,
                padding: "0 2px",
                fontSize: 11.5,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: DS.fontBody,
                color: d.encre,
              }}
            >
              {e.libelle || "Voir"}
              <span aria-hidden="true" style={{ fontSize: 13, opacity: 0.6 }}>
                ›
              </span>
            </button>
          ) : null}
          {/* ── ET QUAND LE SERVEUR A RETIRÉ LE GESTE ────────────────────
                  ⚠️ LE BOUTON DISPARAISSAIT, ET RIEN NE DISAIT POURQUOI
                  (revue du 2026-09-28). Le serveur pose `geste_ferme` depuis
                  l'audit des vagues ; aucun composant ne le lisait. Une
                  annonce qui perd son bouton sans un mot ressemble à une
                  annonce qui n'en a jamais eu — et le fait, lui, reste vrai.
                  Le motif se lit donc ici, en gris : on annonce quand même. */}
          {!e.geste && e.geste_ferme && e.geste_ferme.message ? (
            <span
              data-testid={`evenement-geste-ferme-${e.cle}`}
              style={{
                display: "block",
                marginTop: 6,
                fontSize: 11,
                color: PALETTE_OP.grisD,
              }}
            >
              {e.geste_ferme.message}
            </span>
          ) : null}
        </span>
        {/* ── L'ÉCARTER SANS AGIR (`.opEvL .x`) ─────────────────────── */}
        <button
          type="button"
          className="fbzEvX"
          data-testid={`evenement-ecarter-${e.cle}`}
          onClick={() => sortir(e.cle, (c) => onTraiter && onTraiter(c))}
          title="Traité"
          aria-label={`Traité : ${e.phrase || ""}`}
          style={{
            minWidth: 28,
            minHeight: 28,
            borderRadius: 8,
            border: "none",
            background: "transparent",
            color: PALETTE_OP.grisD,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginTop: 1,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          ✕
        </button>
      </div>
    );
  };

  // ── LE TRAIT FIN (`.opTrait` de la maquette) ───────────────────────────
  // Dimitri, 2026-09-15 : « 18 cartes d'un coup, personne ne les lit. Ce qui
  // presse garde sa carte ; un constat tient sur une ligne — heure, nature,
  // texte. Il reste cliquable et il ne disparaît pas : on ne fait pas
  // s'évaporer une information parce qu'elle est secondaire. »
  const trait = (e) => {
    const part = partants.has(e.cle);
    const d = dessinDe(e);
    return (
      <div
        key={e.cle}
        data-testid={`evenement-${e.cle}`}
        data-trait="1"
        data-famille={e.famille}
        className={`fbzTrait${part ? " part" : ""}`}
        title={String(e.phrase || "").slice(0, 140)}
        role="button"
        tabIndex={0}
        onClick={() => activer(e)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            activer(e);
          }
        }}
      >
        <span className="h">{e.heure || ""}</span>
        <span className="n">{d.etiquette}</span>
        <span className="t">{e.phrase}</span>
        <span className="f" aria-hidden="true">
          ›
        </span>
      </div>
    );
  };

  return (
    <div
      data-testid={testid}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <style>{STYLE}</style>
      {chronologie.map((x) =>
        x.e
          ? x.e.presse === true
            ? ligne(x.e)
            : trait(x.e)
          : rendreTour
            ? rendreTour(x.t, x.i)
            : null,
      )}
    </div>
  );
}
